import { Button, LoadingIndicator } from "@light-technology/components";
import { type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../state/api";
import { errorDetail } from "../../utilities/errors";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";

export const ValidateEmailPage = () => {
  useDocumentTitle("Verify email");

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const hasCalledApi = useRef(false);

  const [validateEmail, validateEmailResult] = api.useValidateEmailMutation({
    // This ensures the mutation is only called once, even with StrictMode
    fixedCacheKey: `validate-email-${token}`,
  });

  useEffect(() => {
    // Use a ref to prevent duplicate calls even with StrictMode
    if (token && validateEmailResult.isUninitialized && !hasCalledApi.current) {
      hasCalledApi.current = true;
      validateEmail({ token });
    }
  }, [token, validateEmail, validateEmailResult.isUninitialized]);

  // ALWAYS prioritize success over errors (in case both are true due to race conditions)
  if (validateEmailResult.isSuccess) {
    return (
      <div className="flex size-full flex-col items-center justify-center">
        <div className="flex max-w-md flex-col gap-6 text-center">
          <div className="text-2xl font-semibold">
            Your email address has been verified
          </div>
          <div className="text-base text-secondary">
            Your email address has been successfully updated.
          </div>
          <Button
            className="w-full"
            size="lg"
            color="primary"
            href="/settings/personal"
          >
            Go to Settings
          </Button>
        </div>
      </div>
    );
  }

  // Check if the error is "Invalid token" which likely means already validated
  const errorMsg = validateEmailResult.error
    ? errorDetail(validateEmailResult.error as FetchBaseQueryError)
    : undefined;
  const isInvalidTokenError = errorMsg === "Invalid token";

  // Show "already verified" page for invalid tokens (but only if not already successful)
  if (isInvalidTokenError) {
    return (
      <div className="flex size-full flex-col items-center justify-center">
        <div className="flex max-w-md flex-col gap-6 text-center">
          <div className="text-2xl font-semibold">
            This link is no longer valid
          </div>
          <div className="text-base text-secondary">
            This verification link has already been used or expired. If you
            still need to change your email, please request a new verification
            link from your profile settings.
          </div>
          <Button
            className="w-full"
            size="lg"
            color="primary"
            href="/settings/personal"
          >
            Go to Settings
          </Button>
        </div>
      </div>
    );
  }

  // Only show error page for genuine errors (not already-validated tokens)
  if (validateEmailResult.isError || !token) {
    return (
      <div className="flex size-full flex-col items-center justify-center">
        <div className="flex max-w-md flex-col gap-6 text-center">
          <div className="text-2xl font-semibold">Verification failed</div>
          <div className="text-base text-secondary">
            The verification link is invalid or has expired. Please try updating
            your email again from your profile settings.
          </div>
          <Button
            className="w-full"
            size="lg"
            color="primary"
            href="/settings/personal"
          >
            Go to Settings
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state while verifying
  return (
    <div className="flex size-full flex-col items-center justify-center">
      <LoadingIndicator size="lg" type="line-spinner" />
    </div>
  );
};
